<template>
  <div>
    <wallet-summary-card
      :balance="walletBalance"
      :kpis="kpis"
      :loading="loadingBalance || loadingTx"
      :filters="filters"
      :sparkline="sparklineData"
      @apply-filters="onApplyFilters"
    >
      <template #actions>
        <b-button
          size="sm"
          variant="outline-secondary"
          @click="exportCsv"
        >
          Exportar CSV
        </b-button>

        <b-button
          v-if="isAdmin"
          v-b-modal.wallet-credit-modal
          size="sm"
          variant="success"
          class="ml-1"
        >
          Abonar
        </b-button>

        <b-button
          v-if="isAdmin"
          v-b-modal.wallet-adjust-modal
          size="sm"
          variant="warning"
          class="ml-1"
        >
          Ajustar
        </b-button>
      </template>

    </wallet-summary-card>

    <wallet-transfer-receipts
      :customer-id="customerId"
      :wallet-id="currentWallet && currentWallet.wallet_id"
      :is-admin="isAdmin"
      :receipts="transferReceipts"
      @refresh-wallet="reload"
    />

    <wallet-transactions-table
      :items="pagedTxs"
      :loading="loadingTx"
      :pagination="paginationForTable"
      @change-page="onChangePage"
      @sort-changed="onSortChanged"
      @refresh="reload"
    />

    <wallet-credit-modal
      id="wallet-credit-modal"
      @confirm="doCredit"
    />
    <wallet-adjust-modal
      id="wallet-adjust-modal"
      @confirm="doAdjust"
    />

  </div>
</template>

<script>
import WalletSummaryCard from '@/views/wallet/WalletSummaryCard.vue'
import WalletTransactionsTable from '@/views/wallet/WalletTransactionsTable.vue'
import WalletCreditModal from '@/views/wallet/WalletCreditModal.vue'
import WalletAdjustModal from '@/views/wallet/WalletAdjustModal.vue'
import WalletTransferReceipts from '@/views/wallet/WalletTransferReceipts.vue'

export default {
  components: {
    WalletSummaryCard,
    WalletTransactionsTable,
    WalletCreditModal,
    WalletAdjustModal,
    WalletTransferReceipts,
  },

  data() {
    return {
      currentWallet: null,
      filters: {
        from: null, to: null, type: null, q: '',
      },
      txPagination: { page: 1, per_page: 50 },
      loadingBalance: false,
      loadingTx: false,
      sort: { field: 'created_at', dir: 'desc' },
    }
  },

  computed: {
    walletTransactionsRaw() { return this.$store.getters['wallet/walletTransactions'] || [] },
    walletBalance() { return this.$store.getters['wallet/walletBalance'] || 0 },

    sparklineData() {
      const txs = this.walletTransactionsRaw
      const byDay = {}
      txs.forEach(t => {
        const day = (t.created_at || '').slice(0, 10)
        const val = this.signedTxAmount(t)
        byDay[day] = (byDay[day] || 0) + val
      })
      const days = Object.keys(byDay).sort().slice(-14)

      let acc = 0
      return days.map(d => {
        acc += byDay[d]
        return acc // ✅ devolver expresión, no asignación
      })
    },

    customerId() {
      const cu = this.$store.getters['auth/currentUser'] || {}
      const p = this.$route.params || {}
      const scopedRoles = Array.isArray(cu.scoped_roles) ? cu.scoped_roles : []
      const customerScopedRole = scopedRoles.find(role => role.role_name === 'customer' && role.role_resource_type === 'Customer')
        || scopedRoles.find(role => role.role_resource_type === 'Customer')
        || scopedRoles[0]
        || null
      const fromRoute = p.customerId || p.userId || p.id || this.$store.getters['customers/selectedCustomerResourceId']
      const fromCurrentUser = customerScopedRole?.role_resource_id || fromRoute
      return cu.role_name === 'admin' ? fromRoute : fromCurrentUser
    },

    // 1) Filtro
    filteredTxs() {
      let items = [...this.walletTransactionsRaw]
      if (this.filters.from) items = items.filter(t => new Date(t.created_at) >= new Date(this.filters.from))
      if (this.filters.to) items = items.filter(t => new Date(t.created_at) <= new Date(this.filters.to))
      if (this.filters.type) items = items.filter(t => t.tx_type === this.filters.type)
      if (this.filters.q) {
        const q = this.filters.q.toLowerCase()
        items = items.filter(t => (t.note || '').toLowerCase().includes(q)
          || (t.reference_type || '').toLowerCase().includes(q)
          || String(t.reference_id || '').includes(q))
      }
      return items
    },

    // 2) Orden
    sortedTxs() {
      const items = [...this.filteredTxs]
      const { field, dir } = this.sort
      items.sort((a, b) => {
        const fa = a[field]; const fb = b[field]
        if (fa == null && fb == null) return 0
        if (fa == null) return 1
        if (fb == null) return -1
        if (fa === fb) return 0
        if (dir === 'asc') return fa > fb ? 1 : -1
        return fa < fb ? 1 : -1
      })
      return items
    },

    // 3) Total filas (para la tabla/paginación)
    totalRows() {
      return this.sortedTxs.length
    },

    // 4) Página actual
    pagedTxs() {
      const start = (this.txPagination.page - 1) * this.txPagination.per_page
      const end = start + this.txPagination.per_page
      return this.sortedTxs.slice(start, end)
    },

    // 5) KPIs (sin efectos colaterales)
    kpis() {
      const credits = this.filteredTxs
        .filter(t => t.tx_type === 'credit')
        .reduce((a, t) => a + Number(t.amount || 0), 0)
      const debits = this.filteredTxs
        .filter(t => t.tx_type === 'debit')
        .reduce((a, t) => a + Number(t.amount || 0), 0)
      const adjustments = this.filteredTxs
        .filter(t => t.tx_type === 'adjustment')
        .reduce((a, t) => a + this.adjustmentDelta(t), 0)
      return {
        credits,
        debits,
        adjustments,
        count: this.filteredTxs.length,
      }
    },

    // 6) Pag. para la tabla (incluye total sin mutar dentro de computed)
    paginationForTable() {
      return {
        page: this.txPagination.page,
        per_page: this.txPagination.per_page,
        total_objects: this.totalRows,
      }
    },

    isAdmin() {
      const cu = this.$store.getters['auth/currentUser'] || {}
      return cu.role_name === 'admin'
    },

    transferReceipts() {
      return this.$store.getters['wallet/transferReceiptsByCustomer'](this.customerId)
    },
  },
  watch: {
    '$route.params.userId': 'hydrate',
    '$route.params.customerId': 'hydrate',
  },

  async created() {
    await this.hydrate()
  },
  activated() {
    this.hydrate()
  },

  methods: {
    adjustmentDelta(tx) {
      const delta = Number(tx && tx.metadata && tx.metadata.delta)
      if (!Number.isNaN(delta)) return delta
      return Number(tx && tx.amount) || 0
    },
    signedTxAmount(tx) {
      if (!tx) return 0
      if (tx.tx_type === 'debit') return -Math.abs(Number(tx.amount || 0))
      if (tx.tx_type === 'credit') return Math.abs(Number(tx.amount || 0))
      if (tx.tx_type === 'adjustment') return this.adjustmentDelta(tx)
      return Number(tx.amount || 0)
    },
    async hydrate() {
      try {
        this.$store.commit('customers/SELECT_CUSTOMER_ID', this.customerId)
      } catch (e) { /* mutación opcional; ignorar si no existe */ }
      await this.reload()
    },

    async reload() {
    // 1) saldo (y obtener wallet_id)
      try {
        this.loadingBalance = true
        this.currentWallet = this.isAdmin
          ? await this.$store.dispatch('wallet/fetchWalletByCustomer', { customerId: this.customerId })
          : await this.$store.dispatch('wallet/fetchWalletMe')
      } finally {
        this.loadingBalance = false
      }

      // 2) movimientos
      try {
        this.loadingTx = true
        const w = this.currentWallet

        if (this.isAdmin && w?.wallet_id) {
          // Admin: por wallet_id
          await this.$store.dispatch('wallet/fetchTransactionsByWalletId', {
            walletId: w.wallet_id,
            limit: 500,
          })
        } else {
          // Cliente: siempre endpoint "me"
          await this.$store.dispatch('wallet/fetchMyTransactions', { limit: 500 })
        }

        this.txPagination.page = 1
      } finally {
        this.loadingTx = false
      }

      // 3) comprobantes
      try {
        const w = this.currentWallet
        if (w?.wallet_id) {
          await this.$store.dispatch('wallet/fetchTransferReceiptsByWalletId', {
            walletId: w.wallet_id,
            customerId: this.customerId,
          })
        }
      } catch (e) {
        console.error('Error cargando comprobantes:', e)
      }
    },

    onApplyFilters(f) { this.filters = f; this.txPagination.page = 1 },
    onChangePage(p) { this.txPagination.page = p },
    onSortChanged({ field, direction }) { this.sort = { field, dir: direction } },

    async doCredit({ amount, note }) {
      try {
        console.log('[doCredit] ->', { amount, note, customerId: this.customerId })
        await this.$store.dispatch('wallet/creditByCustomer', {
          customerId: this.customerId,
          amount,
          note,
        })
        this.$bvToast.toast('Abono realizado', { variant: 'success', solid: true })
        await this.reload()
        this.$bvModal.hide('wallet-credit-modal')
      } catch (e) {
        console.error('Error en doCredit:', e)
        this.$bvToast.toast(e?.response?.data?.error || e?.message || 'Error al abonar', {
          variant: 'danger', solid: true,
        })
      }
    },

    async doAdjust({ delta, note }) {
      try {
        console.log('[doAdjust] ->', { delta, note, customerId: this.customerId })
        await this.$store.dispatch('wallet/adjustByCustomer', {
          customerId: this.customerId,
          delta,
          note,
        })
        this.$bvToast.toast('Ajuste aplicado', { variant: 'warning', solid: true })
        await this.reload()
        this.$bvModal.hide('wallet-adjust-modal')
      } catch (e) {
        console.error('Error en doAdjust:', e)
        this.$bvToast.toast(e?.response?.data?.error || e?.message || 'Error al ajustar', {
          variant: 'danger', solid: true,
        })
      }
    },
    exportCsv() {
      const rows = this.walletTransactionsRaw.map(t => ({
        fecha: t.created_at,
        tipo: t.tx_type,
        monto: t.amount,
        nota: t.note,
        referencia_tipo: t.reference_type,
        referencia_id: t.reference_id,
      }))
      if (!rows.length) return
      const header = Object.keys(rows[0]).join(',')
      const body = rows.map(r => Object.values(r).map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n')
      const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8;' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `wallet-transactions-${this.customerId}.csv`
      a.click()
      URL.revokeObjectURL(a.href)
    },

  },
}
</script>
